import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Users, 
  Calendar, 
  Clock, 
  AlertCircle,
  TrendingUp,
  Activity,
  Phone,
  Home,
  Cake,
  Award,
  PartyPopper,
  Star,
  Copy,
  Mail
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { parseLocalDate } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detect if a string is a UUID — don't display these as role names
const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

// Strip trailing JS-syntax garbage from corrupted historical names (e.g. "John Langer}));")
// Only strips trailing sequences that include '}' or ';' so legitimate names like
// "Alice (Manager)" are preserved.
const sanitizeName = (name) => {
  if (typeof name !== 'string') return name;
  const cleaned = name.replace(/\s*[\)\}\];,>]*[\}\;][\)\}\];,>\s]*$/, '').trim();
  return cleaned || name;
};
// Copy an on-call person's contact details to the clipboard for quick sharing/reaching.
const copyOnCallContact = async ({ name, role, email, workPhone, phone, homePhone }) => {
  const parts = [name || 'On-call'];
  if (role) parts.push(role);
  if (email) parts.push(`Email: ${email}`);
  if (workPhone) parts.push(`Work: ${workPhone}`);
  if (phone) parts.push(`Mobile: ${phone}`);
  if (homePhone) parts.push(`Home: ${homePhone}`);
  try {
    await navigator.clipboard.writeText(parts.join('\n'));
    toast.success(`Copied ${name || 'contact'} details`);
  } catch {
    toast.error('Could not copy to clipboard');
  }
};

const getDisplayRole = (schedule) => {
  const empRole = schedule.employee?.role || schedule.user?.role;
  if (empRole && !isUUID(empRole)) return empRole.toUpperCase();
  const schedRole = schedule.role;
  if (schedRole && !isUUID(schedRole)) return schedRole.toUpperCase();
  return '';
};

const Dashboard = () => {
  const { user } = useAuth();
  
  // Check if user has permission to edit activities (configurable per role in Admin)
  const canEditActivities = user?.permissions?.includes('edit_activities');
  
  const handleSaveActivity = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build the payload with user_ids
      const payload = {
        ...activityForm,
        user_ids: activityForm.user_ids
      };
      
      await axios.put(`${API}/activities/${editingActivity.id}`, payload, { headers });
      toast.success('Activity updated successfully');
      setShowEditActivityModal(false);
      fetchDashboardData(false); // Refresh dashboard data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update activity');
    }
  };
  
  const fetchUsersForEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/employees`, { headers });
      // Filter to users with schedules
      const usersWithSchedules = (response.data || []).filter(u => u.has_schedule !== false);
      setAvailableUsers(usersWithSchedules);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setAvailableUsers([]);
    }
  };
  
  const [stats, setStats] = useState({
    total_employees: 0,
    total_shift_templates: 0,
    today_schedules: 0,
    support_channels: []
  });
  const [recentSchedules, setRecentSchedules] = useState([]);
  const [outToday, setOutToday] = useState([]);
  const [wfhToday, setWfhToday] = useState({ total: 0, by_type: {}, employees: [] });
  const [currentShifts, setCurrentShifts] = useState([]);
  const [onCallPersonnel, setOnCallPersonnel] = useState([]);
  const [upcomingOnCall, setUpcomingOnCall] = useState([]);
  const [todayActivities, setTodayActivities] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [celebrations, setCelebrations] = useState({ today: { birthdays: [], anniversaries: [] }, upcoming: { birthdays: [], anniversaries: [] }, has_celebrations: false });
  const [celebrationsEnabled, setCelebrationsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [endingSoonHours, setEndingSoonHours] = useState(2.0); // Default 2 hours
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track if this is first load
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [selectedActivityParticipants, setSelectedActivityParticipants] = useState([]);
  const [showEditActivityModal, setShowEditActivityModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [activityForm, setActivityForm] = useState({
    title: '',
    activity_type: '',
    start_time: '',
    end_time: '',
    location: '',
    client_name: '',
    description: '',
    user_ids: []
  });

  useEffect(() => {
    fetchSystemSettings();
    fetchDashboardData(true); // Pass true for initial load
    
    // Set up periodic refresh to catch changes from other components
    const refreshInterval = setInterval(() => {
      fetchDashboardData(false); // Pass false for auto-refresh
    }, 30000); // Refresh every 30 seconds
    
    // Refresh when window regains focus (user returns to tab)
    const handleFocus = () => fetchDashboardData(false); // Pass false for focus refresh
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      const settingsResponse = await axios.get(`${API}/system-settings`, { headers });
      if (settingsResponse.data && settingsResponse.data.ending_soon_hours) {
        setEndingSoonHours(settingsResponse.data.ending_soon_hours);
      }
    } catch (error) {
      console.error('Error fetching system settings:', error);
      // Use default value if fetch fails
    }
  };

  const fetchDashboardData = async (isInitial = false) => {
    // Save current scroll position before refresh
    const scrollPosition = window.scrollY;
    
    try {
      // Only show loading spinner on initial load, not on auto-refresh
      if (isInitial) {
        setLoading(true);
      }
      
      // Get authentication token
      let token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found - user should login first');
        toast.error('Authentication required - please login again');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get today's date in local timezone
      const today = new Date();
      const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      // Get upcoming schedules (next 7 days)
      const nextWeekEnd = new Date(today);
      nextWeekEnd.setDate(nextWeekEnd.getDate() + 7);
      const nextWeekEndString = nextWeekEnd.toISOString().split('T')[0];
      
      // Fetch dashboard stats, schedules, and WFH today in parallel
      const [statsResponse, upcomingResponse, wfhTodayResponse] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/schedules`, {
          headers,
          params: {
            date_from: todayString,
            date_to: nextWeekEndString
          }
        }),
        axios.get(`${API}/wfh-today`, { headers }).catch(() => ({ data: { total: 0, by_type: {}, employees: [] } }))
      ]);
      
      setStats(statsResponse.data);
      setWfhToday(wfhTodayResponse.data);
      
      const allSchedules = upcomingResponse.data || [];
      
      // Filter to ensure only today's schedules are shown for recent schedules
      const todaySchedules = allSchedules.filter(schedule => schedule.date === todayString);
      console.log('Dashboard - Today:', todayString, 'All schedules:', allSchedules.length, 'Today only:', todaySchedules.length);
      
      // Extract PTO schedules separately - need all PTOs, not just first 5
      // Also deduplicate PTOs by schedule ID
      const ptoSchedulesMap = new Map();
      todaySchedules.filter(s => s.shift_type === 'pto').forEach(s => {
        if (!ptoSchedulesMap.has(s.id)) {
          ptoSchedulesMap.set(s.id, s);
        }
      });
      const ptoSchedules = Array.from(ptoSchedulesMap.values());
      console.log('Dashboard - PTO schedules today:', ptoSchedules.length);
      
      // Deduplicate todaySchedules for recentSchedules display
      const todaySchedulesMap = new Map();
      todaySchedules.forEach(s => {
        if (!todaySchedulesMap.has(s.id)) {
          todaySchedulesMap.set(s.id, s);
        }
      });
      const uniqueTodaySchedules = Array.from(todaySchedulesMap.values());
      
      setRecentSchedules(uniqueTodaySchedules.slice(0, 5)); // Show only TODAY's schedules (deduplicated)
      setOutToday(ptoSchedules); // Store all PTO schedules for today (deduplicated)
      
      // Process schedules to find active and upcoming on-call
      const activeShifts = [];
      const activeOnCall = [];
      const upcomingOnCallShifts = [];
      
      // Track seen schedule IDs to prevent duplicates
      const seenScheduleIds = new Set();
      
      // DEBUG: Log first schedule to see what data we have
      if (allSchedules.length > 0) {
        console.log('Dashboard Schedule Sample:', {
          employee: allSchedules[0].employee,
          user: allSchedules[0].user,
          role: allSchedules[0].role
        });
      }
      
      const now = new Date();
      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
      
      allSchedules.forEach(schedule => {
        // Skip if we've already processed this schedule ID (deduplication)
        if (seenScheduleIds.has(schedule.id)) {
          console.log('Dashboard - Skipping duplicate schedule:', schedule.id, schedule.employee?.name || schedule.user?.name);
          return;
        }
        seenScheduleIds.add(schedule.id);
        
        // Use simple string comparison instead of Date objects to avoid timezone issues
        const isToday = schedule.date === todayString;
        
        if (schedule.shift_type === 'on_call') {
          if (isToday) {
            // Check if currently active
            const startTime = schedule.start_time.split(':');
            const endTime = schedule.end_time.split(':');
            const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1]);
            const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1]);
            
            // Handle shifts that cross midnight
            const isActive = endMinutes > startMinutes 
              ? (currentTimeMinutes >= startMinutes && currentTimeMinutes <= endMinutes)
              : (currentTimeMinutes >= startMinutes || currentTimeMinutes <= endMinutes);
              
            if (isActive) {
              activeOnCall.push(schedule);
            } else if (startMinutes > currentTimeMinutes) {
              // Later today
              upcomingOnCallShifts.push({...schedule, isToday: true});
            }
          } else {
            // Future days - only include tomorrow and day after tomorrow (next 2 days)
            // Create tomorrow's date string in local timezone
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
            
            // Create day after tomorrow's date string in local timezone
            const dayAfter = new Date(now);
            dayAfter.setDate(dayAfter.getDate() + 2);
            const dayAfterStr = `${dayAfter.getFullYear()}-${String(dayAfter.getMonth() + 1).padStart(2, '0')}-${String(dayAfter.getDate()).padStart(2, '0')}`;
            
            // Only show if schedule date is tomorrow or day after tomorrow
            if (schedule.date === tomorrowStr || schedule.date === dayAfterStr) {
              upcomingOnCallShifts.push({...schedule, isToday: false});
            }
          }
        } else if (isToday && schedule.shift_type !== 'pto') {
          // Regular shifts - only check today for active status, exclude PTO
          const startTime = schedule.start_time.split(':');
          const endTime = schedule.end_time.split(':');
          const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1]);
          const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1]);
          
          const isActive = endMinutes > startMinutes 
            ? (currentTimeMinutes >= startMinutes && currentTimeMinutes <= endMinutes)
            : (currentTimeMinutes >= startMinutes || currentTimeMinutes <= endMinutes);
            
          if (isActive) {
            activeShifts.push(schedule);
          }
        }
      });
      
      // Sort upcoming on-call by date and time
      upcomingOnCallShifts.sort((a, b) => {
        if (a.date !== b.date) {
          return parseLocalDate(a.date) - parseLocalDate(b.date);
        }
        return a.start_time.localeCompare(b.start_time);
      });
      
      // Show all upcoming shifts within the 2-day window (no deduplication)
      // This ensures if someone has multiple shifts in the next 2 days, we see them all
      
      setCurrentShifts(activeShifts);
      setOnCallPersonnel(activeOnCall);
      setUpcomingOnCall(upcomingOnCallShifts.slice(0, 10)); // Show up to 10 upcoming shifts
      
      // Fetch today's activities
      try {
        const [activitiesResponse, activityTypesResponse, celebrationsResponse, featureTogglesResponse] = await Promise.all([
          axios.get(`${API}/activities`, {
            headers,
            params: {
              start_date: todayString,
              end_date: todayString
            }
          }),
          axios.get(`${API}/activity-types`, { headers }),
          axios.get(`${API}/dashboard/celebrations`, { headers }).catch(() => ({ data: { today: { birthdays: [], anniversaries: [] }, upcoming: { birthdays: [], anniversaries: [] }, has_celebrations: false } })),
          axios.get(`${API}/feature-toggles`, { headers }).catch(() => ({ data: { celebrations: true } }))
        ]);
        setTodayActivities(activitiesResponse.data || []);
        setActivityTypes(activityTypesResponse.data || []);
        setCelebrations(celebrationsResponse.data);
        const features = featureTogglesResponse?.data || {};
        setCelebrationsEnabled(features.celebrations !== false);
      } catch (actError) {
        console.error('Failed to fetch activities:', actError);
        // Don't fail the whole dashboard if activities fail
      }
      
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      
      // If authentication fails, redirect to login
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast.error('Session expired - please login again');
        window.location.href = '/login';
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      // Only update loading state on initial load
      if (isInitial) {
        setLoading(false);
        setIsInitialLoad(false);
      }
      
      // Restore scroll position after refresh (for auto-refresh only)
      if (!isInitial && scrollPosition > 0) {
        // Use requestAnimationFrame for smoother scroll restoration
        requestAnimationFrame(() => {
          window.scrollTo({
            top: scrollPosition,
            behavior: 'instant' // Instant jump, no smooth scroll
          });
        });
      }
    }
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate time remaining in shift
  const getTimeRemaining = (endTime) => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    
    const [endHour, endMin] = endTime.split(':').map(Number);
    let endMinutes = endHour * 60 + endMin;
    
    // Handle shifts that end past midnight
    if (endMinutes < currentMinutes) {
      endMinutes += 24 * 60;
    }
    
    const remainingMinutes = endMinutes - currentMinutes;
    return remainingMinutes;
  };

  // Get color scheme based on time remaining
  const getShiftColorScheme = (endTime) => {
    const remainingMinutes = getTimeRemaining(endTime);
    const endingSoonMinutes = endingSoonHours * 60; // Convert configured hours to minutes
    
    // Calculate thresholds based on configured ending_soon_hours
    const redThreshold = endingSoonMinutes * 0.25;      // 25% of window (e.g., 30 min if 2h window)
    const orangeThreshold = endingSoonMinutes * 0.5;    // 50% of window (e.g., 60 min if 2h window)
    const yellowThreshold = endingSoonMinutes;          // Full window (e.g., 120 min if 2h window)
    
    if (remainingMinutes <= redThreshold) {
      // Within 25% of window - RED (ending very soon)
      return {
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        avatarBg: 'bg-red-100',
        avatarText: 'text-red-600',
        badgeBg: 'bg-red-100',
        badgeText: 'text-red-800',
        statusText: 'Ending Soon',
        timeText: `${remainingMinutes} min left`
      };
    } else if (remainingMinutes <= orangeThreshold) {
      // Within 50% of window - ORANGE (ending soon)
      return {
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        avatarBg: 'bg-orange-100',
        avatarText: 'text-orange-600',
        badgeBg: 'bg-orange-100',
        badgeText: 'text-orange-800',
        statusText: 'Ending Soon',
        timeText: `${remainingMinutes} min left`
      };
    } else if (remainingMinutes <= yellowThreshold) {
      // Within full window - YELLOW (nearing end)
      return {
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        avatarBg: 'bg-yellow-100',
        avatarText: 'text-yellow-600',
        badgeBg: 'bg-yellow-100',
        badgeText: 'text-yellow-800',
        statusText: 'On Duty',
        timeText: remainingMinutes < 60 ? `${remainingMinutes} min left` : `${Math.round(remainingMinutes / 60 * 10) / 10}h left`
      };
    } else {
      // Beyond window - GREEN (active)
      return {
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        avatarBg: 'bg-green-100',
        avatarText: 'text-green-600',
        badgeBg: 'bg-green-100',
        badgeText: 'text-green-800',
        statusText: 'On Duty',
        timeText: `${Math.round(remainingMinutes / 60 * 10) / 10}h left`
      };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Shiftela Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Welcome back, {user?.username}! Here's your shift scheduling overview.
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <p className="text-sm text-gray-500">Today</p>
            <p className="text-lg font-semibold text-gray-900">
              {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          <Button 
            onClick={fetchDashboardData} 
            variant="outline" 
            className="flex items-center space-x-2"
            disabled={loading}
          >
            <TrendingUp className="h-4 w-4" />
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </div>
      </div>

      {/* Today-Style Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Now</p>
                <p className="text-3xl font-bold text-gray-900">{currentShifts.length}</p>
                <p className="text-xs text-gray-500 mt-1">Regular shifts</p>
              </div>
              <Users className="h-10 w-10 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">On-Call</p>
                <p className="text-3xl font-bold text-gray-900">{onCallPersonnel.length}</p>
                <p className="text-xs text-gray-500 mt-1">Available now</p>
              </div>
              <AlertCircle className="h-10 w-10 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Out Today</p>
                <p className="text-3xl font-bold text-gray-900">
                  {outToday.length}
                </p>
                <p className="text-xs text-gray-500 mt-1">PTO/Time off</p>
              </div>
              <Calendar className="h-10 w-10 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600" data-testid="wfh-today-label">WFH Today</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="wfh-today-count">{wfhToday.total}</p>
                <div className="flex items-center gap-2 mt-1">
                  {wfhToday.by_type?.flex_bank > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Flex {wfhToday.by_type.flex_bank}</span>
                  )}
                  {wfhToday.by_type?.on_call > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">On-Call {wfhToday.by_type.on_call}</span>
                  )}
                  {wfhToday.by_type?.business_directed > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Directed {wfhToday.by_type.business_directed}</span>
                  )}
                  {wfhToday.total === 0 && (
                    <p className="text-xs text-gray-500">All in office</p>
                  )}
                </div>
              </div>
              <Home className="h-10 w-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Celebrations Section */}
      {celebrationsEnabled && (
        <Card data-testid="celebrations-section" className="shadow-lg border-0 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 opacity-[0.07]">
            <PartyPopper className="w-full h-full text-amber-600" />
          </div>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2.5 text-xl">
              <div className="p-2 rounded-xl bg-amber-100 shadow-sm">
                <PartyPopper className="h-5 w-5 text-amber-600" />
              </div>
              <span className="text-gray-800">Celebrations</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {!celebrations.has_celebrations && (
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 rounded-full bg-amber-100/70 flex items-center justify-center mb-3">
                  <Cake className="h-8 w-8 text-amber-400" />
                </div>
                <p className="text-base font-medium text-gray-600">No birthdays or work anniversaries to show</p>
                {(user?.role === 'admin' || user?.role === 'manager') && (
                  <p className="text-sm text-gray-400 mt-1.5">Add birthday and hire dates in Admin &gt; Edit User to see celebrations here</p>
                )}
              </div>
            )}
            {/* Today's celebrations - prominent */}
            {celebrations.has_celebrations && (celebrations.today.birthdays.length > 0 || celebrations.today.anniversaries.length > 0) && (
              <div className="space-y-3">
                <p className="text-sm font-bold text-amber-700 uppercase tracking-wider">Today</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {celebrations.today.birthdays.map((person) => (
                    <div key={`bday-${person.user_id}`} data-testid={`celebration-birthday-${person.user_id}`}
                      className="flex items-center gap-4 p-4 rounded-2xl bg-white/90 border border-pink-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                      <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-100 to-rose-200 flex items-center justify-center ring-1 ring-pink-200/60">
                        <Cake className="h-8 w-8 text-pink-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-gray-900 text-base truncate">{person.name}</p>
                        <p className="text-sm text-pink-600 font-semibold flex items-center gap-1">
                          <PartyPopper className="h-4 w-4" /> Happy Birthday!
                        </p>
                        {person.job_title && <p className="text-xs text-gray-400 truncate mt-0.5">{person.job_title}</p>}
                      </div>
                    </div>
                  ))}
                  {celebrations.today.anniversaries.map((person) => (
                    <div key={`ann-${person.user_id}`} data-testid={`celebration-anniversary-${person.user_id}`}
                      className="flex items-center gap-4 p-4 rounded-2xl bg-white/90 border border-amber-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                      <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-200 flex items-center justify-center ring-1 ring-amber-200/60">
                        <Award className="h-8 w-8 text-amber-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-gray-900 text-base truncate">{person.name}</p>
                        <p className="text-sm text-amber-600 font-semibold flex items-center gap-1">
                          <Star className="h-4 w-4" /> {person.years} Year Anniversary!
                        </p>
                        {person.job_title && <p className="text-xs text-gray-400 truncate mt-0.5">{person.job_title}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming / windowed celebrations */}
            {celebrations.has_celebrations && (celebrations.upcoming.birthdays.length > 0 || celebrations.upcoming.anniversaries.length > 0) && (
              <div className="space-y-3">
                <p className="text-sm font-bold text-gray-500 uppercase tracking-wider">{celebrations.window_label || 'Upcoming'}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {celebrations.upcoming.birthdays.map((person) => (
                    <div key={`up-bday-${person.user_id}-${person.date}`} data-testid={`upcoming-birthday-${person.user_id}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl bg-white/70 border ${person.is_this_week ? 'border-pink-300 ring-2 ring-pink-200' : 'border-pink-100'} ${person.days_until < 0 ? 'opacity-70' : ''}`}>
                      <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-pink-100 flex items-center justify-center">
                        <Cake className="h-5 w-5 text-pink-500" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-semibold text-gray-800 text-sm truncate">{person.name}</p>
                          {person.is_this_week && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-700 whitespace-nowrap">This week</span>}
                        </div>
                        <p className="text-xs text-gray-400">
                          {new Date(person.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  ))}
                  {celebrations.upcoming.anniversaries.map((person) => (
                    <div key={`up-ann-${person.user_id}-${person.date}`} data-testid={`upcoming-anniversary-${person.user_id}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl bg-white/70 border ${person.is_this_week ? 'border-amber-300 ring-2 ring-amber-200' : 'border-amber-100'} ${person.days_until < 0 ? 'opacity-70' : ''}`}>
                      <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Award className="h-5 w-5 text-amber-500" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-semibold text-gray-800 text-sm truncate">{person.name}</p>
                          {person.is_this_week && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">This week</span>}
                        </div>
                        <p className="text-xs text-gray-400">
                          {person.years}yr &middot; {new Date(person.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Current Status Grid - Dynamic Layout - Priority sections first */}
      <div className={`grid gap-6 ${upcomingOnCall.length > 0 ? 'grid-cols-1 xl:grid-cols-3 lg:grid-cols-2' : 'grid-cols-1 lg:grid-cols-3'}`}>
        {/* Today's Activities - Priority 1 */}
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              <span>Today's Activities</span>
            </CardTitle>
            <CardDescription>Client visits, calls, meetings, and other scheduled activities</CardDescription>
          </CardHeader>
          <CardContent>
            {todayActivities.length > 0 ? (
              <div className="space-y-3">
                {(() => {
                  // Group activities by identical details (title, time, type, location, client)
                  const groupedActivities = [];
                  const activityMap = new Map();
                  
                  todayActivities.forEach(activity => {
                    // Create unique key for identical activities
                    const key = `${activity.title}_${activity.start_time}_${activity.end_time}_${activity.activity_type}_${activity.location || ''}_${activity.client_name || ''}`;
                    
                    if (activityMap.has(key)) {
                      // Add participant to existing group
                      activityMap.get(key).participants.push({
                        id: activity.user_id,
                        name: activity.employee?.name || activity.user?.name || 'Unknown'
                      });
                    } else {
                      // Create new group
                      activityMap.set(key, {
                        ...activity,
                        participants: [{
                          id: activity.user_id,
                          name: activity.employee?.name || activity.user?.name || 'Unknown'
                        }]
                      });
                    }
                  });
                  
                  return Array.from(activityMap.values()).map((activity) => {
                    const activityType = activityTypes.find(t => t.value === activity.activity_type) || {};
                    const isTeamActivity = activity.participants.length > 1;
                    
                    return (
                      <div 
                        key={activity.id} 
                        className="flex items-center justify-between p-3 rounded-lg border"
                        style={{ 
                          backgroundColor: activityType.color + '10',
                          borderColor: activityType.color
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-2xl"
                            style={{ backgroundColor: activityType.color + '20' }}
                          >
                            {activityType.icon || '📅'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{activity.title}</p>
                            {isTeamActivity ? (
                              <div>
                                <p className="text-sm text-gray-600">
                                  👥 Team activity • {activity.participants.length} participants
                                </p>
                                {activity.participants.length <= 10 ? (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {activity.participants.map(p => p.name).join(', ')}
                                  </p>
                                ) : (
                                  <div className="text-xs text-gray-500 mt-1">
                                    <span>{activity.participants.slice(0, 8).map(p => p.name).join(', ')}</span>
                                    <button
                                      onClick={() => {
                                        setSelectedActivityParticipants(activity.participants);
                                        setShowParticipantsModal(true);
                                      }}
                                      className="ml-1 text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                    >
                                      and {activity.participants.length - 8} more
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-600">
                                {activity.participants[0].name}
                                {activity.client_name && ` • ${activity.client_name}`}
                              </p>
                            )}
                            {activity.location && (
                              <p className="text-xs text-gray-500">{activity.location}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex flex-col items-end space-y-2">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {activity.start_time} - {activity.end_time}
                            </p>
                            <span 
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                              style={{ 
                                backgroundColor: activityType.color + '20',
                                color: activityType.color
                              }}
                            >
                              {activityType.name || 'Activity'}
                            </span>
                          </div>
                          {canEditActivities && (
                            <button
                              onClick={() => {
                                setEditingActivity(activity);
                                // Extract user_ids from participants
                                const userIds = activity.participants?.map(p => p.id).filter(Boolean) || [];
                                setActivityForm({
                                  title: activity.title,
                                  activity_type: activity.activity_type,
                                  start_time: activity.start_time,
                                  end_time: activity.end_time,
                                  location: activity.location || '',
                                  client_name: activity.client_name || '',
                                  description: activity.description || '',
                                  user_ids: userIds
                                });
                                fetchUsersForEdit();
                                setShowEditActivityModal(true);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No activities scheduled for today</p>
                <p className="text-sm">Check the Schedule tab to add activities</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Out Today - Priority 2 */}
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-yellow-600" />
              <span>Out Today</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {outToday.length > 0 ? (
              <div className="space-y-3">
                {outToday.map((schedule) => (
                  <div 
                    key={schedule.id} 
                    className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                        <span className="text-yellow-600 font-semibold text-sm">
                          {(schedule.employee?.name || schedule.user?.name)?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{sanitizeName(schedule.employee?.name || schedule.user?.name) || 'Employee'}</p>
                        <p className="text-sm text-gray-600">{getDisplayRole(schedule) || 'PTO'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        PTO
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No one out today</p>
                <p className="text-sm">Full team available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Currently On Call - Priority 3 */}
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <span>Currently On Call</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {onCallPersonnel.length > 0 ? (
              <div className="space-y-3" data-testid="on-call-personnel">
                {onCallPersonnel.map((schedule) => {
                  const workPhone = schedule.employee?.work_phone || schedule.user?.work_phone;
                  const homePhone = schedule.employee?.home_phone || schedule.user?.home_phone;
                  const phone = schedule.employee?.phone || schedule.user?.phone;
                  const email = schedule.employee?.email || schedule.user?.email;
                  const displayPhone = workPhone || phone || homePhone;
                  const ocName = sanitizeName(schedule.employee?.name || schedule.user?.name || getDisplayRole(schedule)) || 'Employee';
                  const ocRole = getDisplayRole(schedule) || 'SUPPORT';
                  
                  return (
                    <div 
                      key={schedule.id} 
                      className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <span className="text-orange-600 font-semibold text-sm">
                            {(schedule.employee?.name || schedule.user?.name)?.charAt(0).toUpperCase() || getDisplayRole(schedule)?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{sanitizeName(schedule.employee?.name || schedule.user?.name || getDisplayRole(schedule)) || 'Employee'}</p>
                          <p className="text-sm text-gray-600">{getDisplayRole(schedule) || 'SUPPORT'}</p>
                          {displayPhone && (
                            <a href={`tel:${displayPhone}`} className="text-sm text-gray-600 hover:text-orange-700 flex items-center mt-1" data-testid={`oncall-phone-${schedule.id}`}>
                              <Phone className="h-3 w-3 mr-1" />
                              {displayPhone}
                            </a>
                          )}
                          {email && (
                            <a href={`mailto:${email}`} className="text-sm text-gray-600 hover:text-orange-700 flex items-center mt-0.5" data-testid={`oncall-email-${schedule.id}`}>
                              <Mail className="h-3 w-3 mr-1" />
                              <span className="truncate max-w-[160px]">{email}</span>
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1.5">
                        <p className="text-sm font-medium text-gray-900">
                          {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                        </p>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          On Call
                        </span>
                        <button
                          onClick={() => copyOnCallContact({ name: ocName, role: ocRole, email, workPhone, phone, homePhone })}
                          data-testid={`copy-oncall-${schedule.id}`}
                          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-orange-700 transition-colors"
                          title="Copy contact"
                        >
                          <Copy className="h-3.5 w-3.5" /> Copy
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              upcomingOnCall.length > 0 ? (
                <div className="space-y-3" data-testid="upcoming-on-call-preview">
                  {/* Clear status banner */}
                  {(() => {
                    const nextShift = upcomingOnCall[0];
                    let countdownText = '';
                    let startLabel = '';
                    if (nextShift) {
                      const now = new Date();
                      let targetDate;
                      if (nextShift.isToday) {
                        targetDate = new Date();
                      } else {
                        targetDate = new Date(nextShift.date + 'T00:00:00');
                      }
                      const [h, m] = nextShift.start_time.split(':').map(Number);
                      targetDate.setHours(h, m, 0, 0);
                      const diffMs = targetDate - now;
                      if (diffMs > 0) {
                        const diffH = Math.floor(diffMs / 3600000);
                        const diffM = Math.floor((diffMs % 3600000) / 60000);
                        if (diffH > 24) {
                          const days = Math.floor(diffH / 24);
                          const remH = diffH % 24;
                          countdownText = `${days}d ${remH}h`;
                        } else if (diffH > 0) {
                          countdownText = `${diffH}h ${diffM}m`;
                        } else {
                          countdownText = `${diffM}m`;
                        }
                      }
                      const isToday = nextShift.isToday;
                      const dateStr = isToday ? 'Today' : new Date(nextShift.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                      startLabel = `${dateStr} at ${formatTime(nextShift.start_time)}`;
                    }
                    return (
                      <div className="px-3 py-2.5 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-amber-400" />
                            <p className="text-sm font-medium text-amber-800">No one is on call right now</p>
                          </div>
                          {countdownText && (
                            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-amber-100 rounded-full border border-amber-300">
                              <Clock className="h-3 w-3 text-amber-600" />
                              <span className="text-xs font-semibold text-amber-700">{countdownText}</span>
                            </div>
                          )}
                        </div>
                        {startLabel && (
                          <p className="text-xs text-amber-600 mt-1 ml-4">Next on-call starts {startLabel}</p>
                        )}
                      </div>
                    );
                  })()}
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Up Next</p>
                  {(() => {
                    // Only show today's upcoming on-call shifts
                    const nextShifts = upcomingOnCall.filter(s => s.isToday);
                    // If none today, show the next day's shifts
                    let shiftsToShow = nextShifts;
                    if (nextShifts.length === 0) {
                      const first = upcomingOnCall[0];
                      shiftsToShow = first ? upcomingOnCall.filter(s => s.date === first.date) : [];
                    }
                    return shiftsToShow.map((schedule) => {
                      const rawName = schedule.employee?.name || schedule.user?.name || getDisplayRole(schedule);
                      const displayName = sanitizeName(rawName);
                      const isToday = schedule.isToday;
                      const dateLabel = isToday ? 'Today' : new Date(schedule.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                      return (
                        <div key={schedule.id} className="flex items-center justify-between p-3 bg-orange-50/50 rounded-lg border border-orange-100">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                              <span className="text-orange-600 font-semibold text-sm">
                                {displayName?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{displayName}</p>
                              <p className="text-xs text-gray-500">{getDisplayRole(schedule)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`inline-block text-xs font-medium px-1.5 py-0.5 rounded mb-0.5 ${isToday ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{dateLabel}</span>
                            <p className="text-sm font-medium text-gray-800">
                              {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                            </p>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No one currently on call</p>
                  <p className="text-sm">No upcoming on-call shifts scheduled</p>
                </div>
              )
            )}
          </CardContent>
        </Card>
      </div>

      {/* Currently On Shift - Moved below (can be long) */}
      <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-green-600" />
              <span>Currently On Shift</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {currentShifts.length > 0 ? (
              <div className="space-y-3" data-testid="current-shifts">
                {currentShifts
                  .slice()
                  .sort((a, b) => {
                    // Primary sort: by time remaining (ascending) - shifts ending soonest at the top
                    const timeRemainingA = getTimeRemaining(a.end_time);
                    const timeRemainingB = getTimeRemaining(b.end_time);
                    
                    if (timeRemainingA !== timeRemainingB) {
                      return timeRemainingA - timeRemainingB;
                    }
                    
                    // Secondary sort: alphabetically by name when time remaining is the same
                    const nameA = (a.employee?.name || a.user?.name || '').toLowerCase();
                    const nameB = (b.employee?.name || b.user?.name || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                  .map((schedule) => {
                  const colorScheme = getShiftColorScheme(schedule.end_time);
                  return (
                    <div 
                      key={schedule.id} 
                      className={`flex items-center justify-between p-3 ${colorScheme.bgColor} rounded-lg border ${colorScheme.borderColor}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 ${colorScheme.avatarBg} rounded-full flex items-center justify-center`}>
                          <span className={`${colorScheme.avatarText} font-semibold text-sm`}>
                            {(schedule.employee?.name || schedule.user?.name)?.charAt(0).toUpperCase() || getDisplayRole(schedule)?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{sanitizeName(schedule.employee?.name || schedule.user?.name || getDisplayRole(schedule)) || 'Employee'}</p>
                          <p className="text-sm text-gray-600">{getDisplayRole(schedule) || 'SUPPORT'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                        </p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorScheme.badgeBg} ${colorScheme.badgeText}`}>
                          <Clock className="h-3 w-3 mr-1" />
                          {colorScheme.statusText}
                        </span>
                        <p className="text-xs text-gray-600 mt-1">{colorScheme.timeText}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No one currently on shift</p>
                <p className="text-sm">Check back during business hours</p>
              </div>
            )}
          </CardContent>
        </Card>

      {/* Upcoming On-Call - Outside grid */}
      {upcomingOnCall.length > 0 && (
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <span>Upcoming On-Call</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="upcoming-on-call">
              {upcomingOnCall.map((schedule) => {
                const workPhone = schedule.employee?.work_phone || schedule.user?.work_phone;
                const homePhone = schedule.employee?.home_phone || schedule.user?.home_phone;
                const phone = schedule.employee?.phone || schedule.user?.phone;
                const displayPhone = workPhone || phone || homePhone;
                
                return (
                  <div 
                    key={schedule.id} 
                    className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <span className="text-orange-600 font-semibold text-sm">
                          {(schedule.employee?.name || schedule.user?.name)?.charAt(0).toUpperCase() || getDisplayRole(schedule)?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{sanitizeName(schedule.employee?.name || schedule.user?.name || getDisplayRole(schedule)) || 'Employee'}</p>
                        <p className="text-sm text-gray-600">{getDisplayRole(schedule) || 'SUPPORT'}</p>
                        {displayPhone && (
                          <p className="text-sm text-gray-600 flex items-center mt-1">
                            <Phone className="h-3 w-3 mr-1" />
                            {displayPhone}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {schedule.isToday ? 'Today' : (() => {
                          // Parse date as local date to avoid timezone shift
                          const [year, month, day] = schedule.date.split('-').map(Number);
                          const localDate = new Date(year, month - 1, day);
                          return localDate.toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric' 
                          });
                        })()}
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                      </p>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Upcoming
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-indigo-600" />
            <span>Quick Actions</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link 
              to="/admin" 
              className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200 cursor-pointer"
              data-testid="quick-action-employees"
            >
              <Users className="h-8 w-8 text-blue-600 mb-2" />
              <h3 className="font-semibold text-gray-900">Manage Employees</h3>
              <p className="text-sm text-gray-600">Add, edit, or view employee information</p>
            </Link>
            
            <Link 
              to="/shift-templates" 
              className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors border border-green-200 cursor-pointer"
              data-testid="quick-action-templates"
            >
              <Clock className="h-8 w-8 text-green-600 mb-2" />
              <h3 className="font-semibold text-gray-900">Shift Templates</h3>
              <p className="text-sm text-gray-600">Create and manage shift templates</p>
            </Link>
            
            <Link 
              to="/schedule" 
              className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors border border-purple-200 cursor-pointer"
              data-testid="quick-action-schedule"
            >
              <Calendar className="h-8 w-8 text-purple-600 mb-2" />
              <h3 className="font-semibold text-gray-900">Schedule Management</h3>
              <p className="text-sm text-gray-600">View and manage team schedules</p>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Today's Schedules - Detailed Coverage View */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            <span>Today's Schedule Details ({new Date().toLocaleDateString()})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentSchedules.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="recent-schedules">
              {recentSchedules.map((schedule) => (
                <div 
                  key={schedule.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">
                        {(schedule.employee?.name || schedule.user?.name)?.charAt(0).toUpperCase() || getDisplayRole(schedule)?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{sanitizeName(schedule.employee?.name || schedule.user?.name || getDisplayRole(schedule)) || 'Employee'}</p>
                      <p className="text-sm text-gray-600">{getDisplayRole(schedule) || 'SUPPORT'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                    </p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      schedule.shift_type === 'on_call' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {schedule.shift_type === 'on_call' ? 'On-Call' : 'Regular'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No schedules for today</p>
              <p className="text-sm">Create schedules to see them here</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Participants Modal */}
      {showParticipantsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowParticipantsModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                Activity Participants ({selectedActivityParticipants.length})
              </h3>
              <button
                onClick={() => setShowParticipantsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {selectedActivityParticipants.map((participant, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">
                        {participant.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{participant.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowParticipantsModal(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Activity Modal */}
      {showEditActivityModal && editingActivity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowEditActivityModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Edit Activity</h3>
              <button
                onClick={() => setShowEditActivityModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                {/* Editable Participants Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Participants ({activityForm.user_ids.length} selected)
                  </label>
                  <div className="border border-gray-200 rounded-md p-3 max-h-40 overflow-y-auto bg-gray-50">
                    {availableUsers.length > 0 ? (
                      <div className="space-y-2">
                        {availableUsers.map((u) => (
                          <label key={u.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={activityForm.user_ids.includes(u.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setActivityForm({
                                    ...activityForm,
                                    user_ids: [...activityForm.user_ids, u.id],
                                  });
                                } else {
                                  setActivityForm({
                                    ...activityForm,
                                    user_ids: activityForm.user_ids.filter(id => id !== u.id)
                                  });
                                }
                              }}
                              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{u.name || u.username || u.email}</span>
                            {u.role && <span className="text-xs text-gray-400">({u.role})</span>}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Loading users...</p>
                    )}
                  </div>
                  {activityForm.user_ids.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">Please select at least one participant</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={activityForm.title}
                    onChange={(e) => setActivityForm({...activityForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Activity Type *</label>
                  <select
                    value={activityForm.activity_type}
                    onChange={(e) => setActivityForm({...activityForm, activity_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {activityTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.icon} {type.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                    <input
                      type="time"
                      value={activityForm.start_time}
                      onChange={(e) => setActivityForm({...activityForm, start_time: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
                    <input
                      type="time"
                      value={activityForm.end_time}
                      onChange={(e) => setActivityForm({...activityForm, end_time: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={activityForm.location}
                    onChange={(e) => setActivityForm({...activityForm, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Office, client site, etc."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                  <input
                    type="text"
                    value={activityForm.client_name}
                    onChange={(e) => setActivityForm({...activityForm, client_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="For client-facing activities"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={activityForm.description}
                    onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Additional details..."
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditActivityModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveActivity}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;